import React, { FC } from "react";
import { Helmet } from "react-helmet";
import CookieSettings from "components/CookieSettings/CookieSettings";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
// Removed CookieIcon import as it doesn't exist in @heroicons/react/24/outline"

export interface CookiesProps {
  className?: string;
}

const Cookies: FC<CookiesProps> = ({ className = "" }) => {
  return (
    <div className={`nc-CookiesPage relative ${className}`}>
      <Helmet>
        <title>Gestion des Cookies - Event To Bee</title>
        <meta
          name="description"
          content="Gérez vos préférences de cookies et découvrez notre politique de confidentialité conforme au RGPD."
        />
        <link rel="canonical" href="https://eventtobee.com/cookies" />
      </Helmet>

      <BgGlassmorphism />

      <div className="container relative pt-10 pb-16 lg:pt-20 lg:pb-28">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center items-center gap-4 mb-6">
            <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-full">
              <span className="text-4xl">🍪</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">Gestion des Cookies</h1>
          </div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Découvrez comment Event To Bee utilise les cookies et personnalisez vos préférences en toute
            transparence, conformément au RGPD.
          </p>
        </div>

        {/* Cookie Settings Component */}
        <div className="mb-16">
          <CookieSettings standalone={true} />
        </div>

        {/* Detailed Information */}
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Qu'est-ce qu'un cookie */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">🍪 Qu'est-ce qu'un cookie ?</h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Un cookie est un petit fichier texte stocké sur votre appareil lors de votre visite sur un site web.
              Les cookies nous permettent de reconnaître votre navigateur et de capturer et mémoriser certaines
              informations. Ils améliorent votre expérience de navigation et nous aident à analyser le trafic de
              notre site.
            </p>
          </div>

          {/* Comment nous utilisons les cookies */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              🔧 Comment Event To Bee utilise les cookies
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">🔒 Cookies essentiels</h3>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Requis pour la sécurité, l'authentification et le fonctionnement de base du site.
                  </p>
                </div>

                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">⚙️ Cookies fonctionnels</h3>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Mémorisent vos préférences (thème, langue) pour une expérience personnalisée.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <h3 className="font-semibold text-purple-800 dark:text-purple-200 mb-2">📊 Cookies d'analyse</h3>
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    Collectent des données anonymes pour améliorer nos services et comprendre l'usage.
                  </p>
                </div>

                <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                  <h3 className="font-semibold text-orange-800 dark:text-orange-200 mb-2">🎯 Cookies marketing</h3>
                  <p className="text-sm text-orange-700 dark:text-orange-300">
                    Permettent d'afficher des publicités pertinentes et mesurer leur efficacité.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Vos droits RGPD */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">⚖️ Vos droits selon le RGPD</h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Vous avez le droit de :</h3>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    Consentir ou refuser les cookies non essentiels
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    Modifier vos préférences à tout moment
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    Accéder à vos données personnelles
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    Demander l'effacement de vos données
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Comment exercer vos droits :</h3>
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    📧 Contactez-nous à :
                    <a href="mailto:privacy@eventtobee.com" className="text-blue-600 hover:underline ml-1">
                      privacy@eventtobee.com
                    </a>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">📞 Appelez-nous : 09 77 93 30 63</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    ⚙️ Utilisez le panneau de configuration ci-dessus
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center pt-8 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Dernière mise à jour : {new Date().toLocaleDateString("fr-FR")}
            </p>
            <div className="flex justify-center gap-6 text-sm">
              <a href="/privacy" className="text-blue-600 hover:underline">
                Politique de confidentialité
              </a>
              <a href="/terms" className="text-blue-600 hover:underline">
                Conditions d'utilisation
              </a>
              <a href="/contact" className="text-blue-600 hover:underline">
                Nous contacter
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    /*       <div className="dark:bg-gray-800 ">
            <div className="container ">
                <div className="lg:flex row mb:flex row ">
                    <div className="w-full lg:w-1/2 lg:mt-52 ">
                        <h4 className="my-8 flex items-center text-1xl leading-[40%] md:text-5xl md:leading-[80%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
                            Mentions légales  
                        </h4>
                        <p className="mb-2 font-thin text-gray-900 dark:text-white items-center justify-center pt-5 text-center">
                            Ce site est édité par la société Bigup-conseils :
                        </p>
                    </div>
                    <div className="w-full lg:w-1/2 mb-32 ">
                        <div className="sm:mt-4 lg:pl-16 lg:pt-6 col-lg-6">
                            <div className="   ">
                                <article>
                                    <ul>
                                        <li>Société par Actions Simplifiée au capital de 1000 €</li>
                                        <li>Siège social : 7 rue de l'industrie 92400 courbevoie</li>
                                        <li>RCS Nanterre B 984 189 571</li>
                                        <li>N° TVA intracommunautaire : FR81984189571</li>
                                        <li>Téléphone : 09 77 93 30 63 (Appel non surtaxé)</li>
                                        <li>Contact : support@eventtobee.com</li>
                                        <li>Directeur de la publication : Soukaina CHOUARI</li>
                                    </ul>
                                    <ul>
                                        <li>Société par Actions Simplifiée au capital de 1000 €</li>
                                        <li>Siège social : 7 rue de l'industrie 92400 courbevoie</li>
                                        <li>RCS Nanterre B 984 189 571</li>
                                    </ul>
                                </article>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div> */
    /*      <div className=" flex items-center justify-center flex-col bg-[#E5E5E5] pt-1 pb-6 ">
            <h4 className="my-8 flex items-center text-2xl leading-[80%] md:text-5xl md:leading-[80%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
                Gestion des cookies
            </h4>

            <div className="rounded-xl  flex flex-col ">
                <div className="  lg:w-5/5 pr-4 pl-4 md:mx-3/5 ">
                    <div className="flex items-stretch max-w-4xl p-6 mb-4 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">
                        <div className="   ">
                            <p className="mb-2 font-semibold text-gray-900 dark:text-white items-center justify-center pt-5 text-center">
                                Ce site est édité par la société Bigup-conseils :
                            </p>

                            <article>
                                <ul>
                                    <li>Société par Actions Simplifiée au capital de 1000 €</li>
                                    <li>Siège social : 7 rue de l'industrie 92400 courbevoie</li>
                                    <li>RCS Nanterre B 984 189 571</li>
                                    <li>N° TVA intracommunautaire : FR81984189571</li>
                                    <li>Téléphone : 09 77 93 30 63 (Appel non surtaxé)</li>
                                    <li>Contact : support@eventtobee.com</li>
                                    <li>Directeur de la publication : Soukaina CHOUARI</li>
                                </ul>
                                <ul>
                                    <li>Société par Actions Simplifiée au capital de 1000 €</li>
                                    <li>Siège social : 7 rue de l'industrie 92400 courbevoie</li>
                                    <li>RCS Nanterre B 984 189 571</li>
                                </ul>
                            </article>
                        </div>
                    </div>
                </div>
            </div>
        </div> */
  );
};

export default Cookies;
